import { type ApiFetchContext, apiFetch } from "../client";
import {
  buildTelegramInitDataLoginUrl,
  buildTelegramOauth2Url,
  buildThirdLoginBindUrl,
  buildThirdLoginCallbackUrl,
  buildThirdLoginRenderUrl,
  type ThirdLoginRenderInput,
  type ThirdSource,
} from "../path-adapter";
import type { LoginVo } from "../types/user";

/**
 * Resolves the third-party OAuth authorization URL for a login/register flow
 * (aps-kupikupi #239).
 *
 * The backend `GET /sys/thirdLogin/render/{source}` returns a JSON envelope —
 * `{ success, code:200, result:"<oauth url>" }` — NOT an HTTP 302. A naive
 * `window.location.href = renderEndpoint` therefore navigated the browser to
 * the render endpoint and rendered the JSON text instead of bouncing to the
 * provider's consent screen.
 *
 * So we fetch the envelope here, let `apiFetch` unwrap `result`, and hand the
 * caller the bare OAuth URL to redirect to. Carry-through params (lang,
 * callback, inviteCode, …) are built into the render path by
 * `buildThirdLoginRenderUrl`.
 *
 * NOTE: the cleaner long-term fix is on the backend (make the endpoint return
 * 302). A companion aps-boot issue tracks that; this FE fetch is the bridge
 * until then.
 */
export async function renderThirdLogin(
  ctx: ApiFetchContext,
  input: ThirdLoginRenderInput,
): Promise<string> {
  return apiFetch<string>(ctx, buildThirdLoginRenderUrl(input));
}

export interface CompleteThirdLoginInput {
  source: ThirdSource;
  code: string;
  state?: string;
}

/**
 * Completes a render→redirect OAuth login by exchanging the provider's
 * `code`/`state` for a session (`LoginVo.xaccessToken`) — the step the
 * `/auth/callback` page must perform (aps-kupikupi #649). The render flow
 * stored lang / inviteCode / redirect_uri in the backend context (keyed by
 * `state`), so only `code`+`state` are needed here.
 *
 *   - google / yandex → `GET  /sys/thirdLogin/{source}/callback?code=&state=`
 *   - telegram        → `POST /sys/thirdLogin/telegram/oauth2 {code,state}`
 *
 * Returns the `LoginVo`; callers read `xaccessToken` (and `bindEmailRequired`
 * for the Telegram-without-email edge case).
 */
export async function completeThirdLogin(
  ctx: ApiFetchContext,
  input: CompleteThirdLoginInput,
): Promise<LoginVo> {
  if (input.source === "telegram") {
    return apiFetch<LoginVo>(ctx, buildTelegramOauth2Url(), {
      method: "POST",
      body: { code: input.code, state: input.state },
    });
  }
  return apiFetch<LoginVo>(ctx, buildThirdLoginCallbackUrl(input));
}

/**
 * Provider credential carried in the bind request body.
 *
 * Mirrors what the backend `verifyThirdIdentity` reads per source
 * (aps-boot #216 / #275 bind-mode):
 *   - google   → `{ code }`            (access-token used against Google userinfo)
 *   - yandex   → `{ code, callback }`  (server re-exchanges the code; `callback`
 *                                       must equal the render redirect_uri)
 *   - telegram → `{ initData }`        (Mini App initData — the Mini App path)
 *             or `{ state, code }`     (Web OIDC — the web bind-callback path, #275)
 *
 * VK is not bindable yet (no FE-obtainable credential); Apple was removed
 * platform-wide and never reaches this path.
 */
export type ThirdLoginBindCredential =
  | { code: string; callback?: string }
  | { initData: string }
  | { state: string; code: string };

/**
 * `POST /sys/thirdLogin/bind/{source}` (aps-boot #216, signature-verified route #220).
 *
 * Binds a third-party provider to the *currently authenticated* user (does NOT
 * create or switch sessions — that's the login `render` flow). Requires the
 * caller's signed JWT, sent as `X-Access-Token` via `authToken`.
 *
 * The caller obtains `credential` from the bind-mode OAuth round-trip (#275):
 * a logged-in user starts the same `render` flow but points its `callback` at
 * the bind-callback landing page; the provider redirects back there with the
 * raw `code`/`state`, which the page posts here (google `{code}`, yandex
 * `{code, callback}`, telegram web `{state, code}`, Mini App `{initData}`).
 */
export async function bindThirdLogin(
  ctx: ApiFetchContext,
  authToken: string,
  source: ThirdSource,
  credential: ThirdLoginBindCredential,
): Promise<string> {
  return apiFetch<string>(ctx, buildThirdLoginBindUrl(source), {
    method: "POST",
    body: credential,
    authToken,
  });
}

/** Body for the Telegram Mini App silent-login exchange. */
export interface TelegramInitDataLoginInput {
  /** Raw, unmodified `initData` string from the TG WebView (`retrieveRawInitData`). */
  initData: string;
  /** Local referral code, if any. Backend binds it when Mini App auto-registers a new user. */
  inviteCode?: string;
}

/**
 * `POST /sys/thirdLogin/telegram/initData` — exchange a Telegram Mini App
 * `initData` string for a session. Returns the same `LoginVo` shape as
 * `/user/login`, so `result.xaccessToken` is the JWT to persist and send as
 * `X-Access-Token` on every subsequent call. Unauthenticated request (no
 * `authToken`): this is the call that *creates* the session.
 */
export async function telegramMiniAppLogin(
  ctx: ApiFetchContext,
  input: TelegramInitDataLoginInput,
): Promise<LoginVo> {
  const body: Record<string, string> = { initData: input.initData };
  if (input.inviteCode) body.inviteCode = input.inviteCode;
  return apiFetch<LoginVo>(ctx, buildTelegramInitDataLoginUrl(), {
    method: "POST",
    body,
  });
}
